<script lang="ts">
  // PageOverview — `/#/` (KPI row + host overview + unhealthy alerts).
  //
  // Visual reference: openspec/changes/ui-redesign-v2/Untitled.fig
  // nodes 0:81-174.
  //
  // Subscribes to the WS singleton's snapshot store (defined in
  // `lib/ws.ts`) and re-derives KPIs on every frame. The store is
  // the only data source — no hardcoded values anywhere in this
  // component.

  import { onDestroy, onMount } from "svelte";
  import MetricCard from "./MetricCard.svelte";
  import LineChart from "./LineChart.svelte";
  import GroupManager from "./GroupManager.svelte";
  import { createWsClient, type WsClient, type SnapshotState } from "../lib/ws";
  import { formatNumber, formatPercent } from "../lib/format";
  import { getGroups, type GroupsMap } from "../lib/api";
  import type { ContainerMetrics } from "../lib/bindings";

  // Browser-only singleton. The factory opens a WS; in SSR / tests
  // the mock in `page-overview.test.ts` returns a controllable store.
  const isBrowser = typeof window !== "undefined";
  const client: WsClient | null = isBrowser ? createWsClient() : null;
  if (client) client.connect();

  let snapshot: SnapshotState = client
    ? initialFrom(client)
    : emptySnapshot();
  let unsub: (() => void) | null = null;

  function emptySnapshot(): SnapshotState {
    return {
      host: null,
      containers: [],
      logLines: [],
      connectionState: "disconnected",
      lastError: null,
      lastUpdateMs: null,
      hostHistory: [],
      ramHistory: [],
      cpuHistory: {},
    };
  }

  function initialFrom(c: WsClient): SnapshotState {
    // Pull the current value synchronously via the pub-sub contract.
    let v: SnapshotState = emptySnapshot();
    const u = c.snapshot.subscribe((next) => {
      v = next;
    });
    u();
    return v;
  }

  if (client) {
    unsub = client.snapshot.subscribe((next) => {
      snapshot = next;
    });
  }

  onDestroy(() => {
    unsub?.();
  });

  // Reactive derivations from the live snapshot.
  $: containers = snapshot.containers ?? [];
  $: host = snapshot.host;
  $: connection = snapshot.connectionState;

  // KPI derivations — pure functions of the snapshot.
  $: total = containers.length;
  $: healthy = containers.filter((c) => c.state === "running").length;
  $: unhealthy = containers.filter((c) => c.state !== "running").length;
  $: healthyPct = total === 0 ? 0 : Math.round((healthy / total) * 100);
  $: unhealthyContainers = containers.filter((c) => c.state !== "running");

  // --- App groups ---------------------------------------------------------
  // Groups are operator-defined (GroupManager → /api/groups); membership
  // is computed client-side from the `group` field the agent stamps on
  // every ContainerMetrics at publish time.
  let groups: GroupsMap = {};
  let groupsTick = 0;
  onMount(() => {
    void getGroups().then((g) => (groups = g)).catch(() => {});
  });
  function reloadGroups(): void {
    groupsTick += 1;
    void getGroups()
      .then((g) => {
        groups = g;
        groupsTick = groupsTick; // keep reactive link alive
      })
      .catch(() => {});
  }

  $: groupedApps = buildGroups(containers, groups, groupsTick);
  $: ungrouped = containers.filter((c) => !c.group);

  function buildGroups(
    all: ContainerMetrics[],
    map: GroupsMap,
    _tick: number,
  ): Array<{ name: string; members: ContainerMetrics[] }> {
    void _tick;
    return Object.keys(map)
      .sort()
      .map((name) => ({
        name,
        members: all.filter((c) => c.group === name),
      }));
  }

  // Collapsed-section UI state.
  let collapsed: Record<string, boolean> = {};
  function toggleGroup(name: string): void {
    collapsed = { ...collapsed, [name]: !collapsed[name] };
  }

  // RAM % series for the chart (cpu history comes straight off the store).
  $: ramSeries = snapshot.ramHistory;

  // Host metrics grid (CPU / RAM / disk) — only render when host
  // metrics have arrived in a frame.
  $: cpuValue = host ? formatPercent(host.cpu_pct) : "—";
  $: ramUsed = host?.ram_used ?? 0;
  $: ramTotal = host?.ram_total ?? 0;
  $: ramPct = host && host.ram_total > 0
    ? Math.round((host.ram_used / host.ram_total) * 100)
    : 0;
  $: ramValue = host ? formatPercent(ramPct) : "—";
  $: ramDetail = host
    ? `${formatNumber(Math.round(ramUsed / 1024 ** 2))} / ${formatNumber(Math.round(ramTotal / 1024 ** 2))} MiB`
    : "—";
  $: diskPct = host && host.disks.length > 0
    ? Math.round((host.disks[0]!.used / host.disks[0]!.total) * 100)
    : 0;
  $: diskValue = host && host.disks.length > 0 ? formatPercent(diskPct) : "—";
  $: diskDetail = host && host.disks.length > 0
    ? `${formatNumber(Math.round(host.disks[0]!.used / 1024 ** 3))} / ${formatNumber(Math.round(host.disks[0]!.total / 1024 ** 3))} GiB`
    : "—";

  function healthyPctValue(): string {
    return formatPercent(healthyPct);
  }

  function describeContainer(c: ContainerMetrics): string {
    return `${c.name} · ${c.state}`;
  }
</script>

<section class="page-overview" aria-labelledby="overview-heading">
  <h2 id="overview-heading" class="page-overview__title">Overview</h2>

  <div class="page-overview__kpi-row" role="list" aria-label="Cluster KPIs">
    <MetricCard label="Total" value={String(total)} />
    <MetricCard label="Healthy" value={String(healthy)} status="ok" />
    <MetricCard label="Unhealthy" value={String(unhealthy)} status={unhealthy > 0 ? "err" : "ok"} />
    <MetricCard label="Health" value={healthyPctValue()} status={healthyPct >= 99 ? "ok" : "warn"} />
  </div>

  <div class="page-overview__host" aria-labelledby="host-overview-heading">
    <h3 id="host-overview-heading" class="page-overview__section-title">Host overview</h3>
    <div class="page-overview__host-grid">
      <article class="host-tile" data-testid="host-cpu">
        <header class="host-tile__head">
          <span class="host-tile__label">CPU</span>
          <span class="host-tile__value">{cpuValue}</span>
        </header>
        <LineChart series={snapshot.hostHistory} max={100} unit="%" label="Host CPU history" />
      </article>
      <article class="host-tile" data-testid="host-ram">
        <header class="host-tile__head">
          <span class="host-tile__label">RAM</span>
          <span class="host-tile__value">{ramValue}</span>
        </header>
        <LineChart series={ramSeries} max={100} unit="%" label="Host RAM history" />
        <p class="host-tile__detail">{ramDetail}</p>
      </article>
      <article class="host-tile" data-testid="host-disk">
        <header class="host-tile__head">
          <span class="host-tile__label">Disk</span>
          <span class="host-tile__value">{diskValue}</span>
        </header>
        <p class="host-tile__detail">{diskDetail}</p>
      </article>
    </div>
  </div>

  <section class="page-overview__apps" aria-labelledby="apps-heading">
    <header class="page-overview__section-head">
      <h3 id="apps-heading" class="page-overview__section-title">Apps</h3>
    </header>
    <div class="page-overview__apps-groups" data-testid="apps-sections">
      {#if groupedApps.length === 0}
        <p class="page-overview__empty-note">
          Sin grupos definidos — usa el gestor de abajo para agrupar front + back + db como una app.
        </p>
      {:else}
        {#each groupedApps as app (app.name)}
          <article class="app-group" data-testid={"app-group-" + app.name}>
            <button
              type="button"
              class="app-group__head"
              aria-expanded={!collapsed[app.name]}
              on:click={() => toggleGroup(app.name)}
            >
              <span class="app-group__name mono">{app.name}</span>
              <span class="app-group__meta">
                {app.members.filter((m) => m.state === "running").length}/{app.members.length} up
              </span>
              <span class="app-group__chevron" aria-hidden="true">
                {collapsed[app.name] ? "▸" : "▾"}
              </span>
            </button>
            {#if !collapsed[app.name]}
              <ul class="app-group__members">
                {#each app.members as m (m.id)}
                  <li class="app-member" data-state={m.state}>
                    <a
                      class="app-member__name"
                      href={`/containers/${encodeURIComponent(m.name)}`}
                    >{m.name}</a>
                    <span class="app-member__state">{m.state}</span>
                    <span class="app-member__cpu mono">{formatPercent(m.cpu_pct)}</span>
                  </li>
                {:else}
                  <li class="app-member app-member--empty">sin contenedores visibles</li>
                {/each}
              </ul>
            {/if}
          </article>
        {/each}
      {/if}
      {#if ungrouped.length > 0}
        <article class="app-group" data-testid="ungrouped-section">
          <button
            type="button"
            class="app-group__head"
            aria-expanded={!collapsed["__ungrouped"]}
            on:click={() => toggleGroup("__ungrouped")}
          >
            <span class="app-group__name">Otros contenedores</span>
            <span class="app-group__meta">{ungrouped.length}</span>
            <span class="app-group__chevron" aria-hidden="true">
              {collapsed["__ungrouped"] ? "▸" : "▾"}
            </span>
          </button>
          {#if !collapsed["__ungrouped"]}
            <ul class="app-group__members">
              {#each ungrouped as m (m.id)}
                <li class="app-member" data-state={m.state}>
                  <a
                    class="app-member__name"
                    href={`/containers/${encodeURIComponent(m.name)}`}
                  >{m.name}</a>
                  <span class="app-member__state">{m.state}</span>
                  <span class="app-member__cpu mono">{formatPercent(m.cpu_pct)}</span>
                </li>
              {/each}
            </ul>
          {/if}
        </article>
      {/if}
    </div>
  </section>

  <GroupManager onGroupsChanged={reloadGroups} />

  <section class="page-overview__unhealthy" aria-labelledby="unhealthy-heading">
    <header class="page-overview__section-head">
      <h3 id="unhealthy-heading" class="page-overview__section-title">Unhealthy containers</h3>
      {#if connection === "disconnected"}
        <span class="page-overview__stale" role="status">Stale — last frame retained</span>
      {/if}
    </header>
    <div class="page-overview__unhealthy-list" data-testid="unhealthy-section">
      {#if unhealthyContainers.length === 0}
        <div class="unhealthy-card unhealthy-card--empty" data-empty="true">
          <span class="unhealthy-card__icon" aria-hidden="true">✓</span>
          <span class="unhealthy-card__name">All containers healthy</span>
        </div>
      {:else}
        {#each unhealthyContainers as c (c.id)}
          <article class="unhealthy-card" data-state={c.state}>
            <span class="unhealthy-card__icon" aria-hidden="true">!</span>
            <span class="unhealthy-card__name">{c.name}</span>
            <span class="unhealthy-card__state">{c.state}</span>
            <span class="unhealthy-card__detail">{describeContainer(c)}</span>
          </article>
        {/each}
      {/if}
    </div>
  </section>
</section>
